import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const SMSPreviewModal = ({ 
  isOpen, 
  onClose, 
  modalData, 
  onTemplateChange, 
  onMessageEdit, 
  onSend 
}) => {
  const [scheduleForLater, setScheduleForLater] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  useEffect(() => {
    if (isOpen) { setScheduleForLater(false); setScheduledDateTime(''); }
  }, [isOpen, modalData.petId, modalData.stepKey]);
  const sendDisabled = modalData.sending || modalData.loading || !modalData.renderedBody?.trim() || (scheduleForLater && !scheduledDateTime);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" data-testid="sms-preview-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">📱 Send SMS</h2>
              <p className="text-sm text-purple-100 mt-1">
                {modalData.clientName} - {modalData.petName}
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="sms-modal-close" aria-label="Close SMS preview" disabled={modalData.sending}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-4">
          {modalData.loading && (
            <div className="text-center py-8" data-testid="sms-preview-loading">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading template...</p>
            </div>
          )}
          
          {modalData.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert" data-testid="sms-preview-error">
              <p className="text-red-800 font-medium">❌ Error</p>
              <p className="text-red-600 text-sm mt-1">{modalData.error}</p>
              <button
                onClick={() => onSend(scheduleForLater ? scheduledDateTime : null)}
                data-testid="sms-send-retry" disabled={sendDisabled}
                className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Retry
              </button>
            </div>
          )}
          
          {modalData.success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center" role="status" data-testid="sms-send-confirmation">
              <p className="text-green-800 font-medium text-lg">{modalData.scheduled ? '✓ SMS scheduled' : '✓ SMS sent successfully'}</p>
              <p className="text-green-600 text-sm mt-1">{modalData.scheduled ? 'The step will complete after the SMS is sent.' : 'The workflow step has been marked as completed.'}</p>
            </div>
          )}
          
          {!modalData.loading && !modalData.success && (
            <>
              {/* Template Selector */}
              {modalData.alternativeTemplates && modalData.alternativeTemplates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📋 Template
                  </label>
                  <select
                    value={modalData.templateKey}
                    onChange={(e) => onTemplateChange(e.target.value)}
                    data-testid="sms-template-select" aria-label="SMS template" disabled={modalData.sending}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={modalData.primaryTemplate?.key}>
                      {modalData.primaryTemplate?.name} (Default)
                    </option>
                    {modalData.alternativeTemplates.map(template => (
                      <option key={template.key} value={template.key}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Recipient Info */}
              <div className="bg-gray-50 rounded-lg p-4" data-testid="sms-recipient-details">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">To:</span> {modalData.toPhone}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Client:</span> {modalData.clientName}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Pet:</span> {modalData.petName}
                </p>
              </div>
              
              {/* Message Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  💬 Message (editable)
                </label>
                <textarea
                  value={modalData.renderedBody}
                  onChange={(e) => onMessageEdit(e.target.value)}
                  data-testid="sms-message-input" aria-label="SMS message" disabled={modalData.sending}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-sans text-sm"
                  placeholder="SMS message..."
                />
                <p className="text-xs text-gray-500 mt-1" data-testid="sms-character-count">
                  {modalData.renderedBody.length} characters
                </p>
              </div>
              
              {/* Schedule for Later Toggle */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleForLater}
                    onChange={(e) => setScheduleForLater(e.target.checked)}
                    data-testid="sms-schedule-toggle" disabled={modalData.sending}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    📅 Schedule for later
                  </span>
                </label>
                
                {scheduleForLater && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🕐 Send Date & Time (AEST)
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduledDateTime}
                      onChange={(e) => setScheduledDateTime(e.target.value)}
                      data-testid="sms-scheduled-datetime" aria-label="SMS send date and time AEST" disabled={modalData.sending}
                      step="1800"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Time will be in 30-minute intervals. SMS will be sent automatically at the scheduled time.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Send Button */}
              <div className="flex gap-3">
                <button
                  onClick={() => onSend(scheduleForLater ? scheduledDateTime : null)}
                  data-testid="sms-send-button"
                  disabled={sendDisabled}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium text-white
                    ${sendDisabled
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                    }`}
                >
                  {modalData.sending ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {scheduleForLater ? 'Scheduling...' : 'Sending...'}
                    </span>
                  ) : (
                    scheduleForLater ? '📅 Schedule SMS' : '📤 Send Now'
                  )}
                </button>
                <button
                  onClick={onClose}
                  disabled={modalData.sending}
                  data-testid="sms-preview-cancel"
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
              
              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-testid="sms-send-notice">
                <p className="text-xs text-yellow-800">
                  ⚠️ <span className="font-medium">Note:</span> Once sent, the SMS will be delivered to the client's phone 
                  and the workflow step will be automatically marked as completed.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// Email Preview Modal Component

export default SMSPreviewModal;
